import { createSlice } from "@reduxjs/toolkit";
import { api } from "utils";
import { getCalendar } from "utils/date";

const initialState = {
  todaysReservations: {},
  todaysSales: {},
  todaysVouchers: {},
  receivableSum: null,
  appLinkCount: null,
  qnaCount: null,

  fromTop: 0,
  loading: false,
  errors: {
    reservation: null,
    sales: null,
    vouchers: null,
    receivable: null,
  },
};

const home = createSlice({
  name: "home",
  initialState,
  reducers: {
    fetchHomeDataStart: (state) => {
      state.loading = true;
    },

    setReservations: (state, action) => {
      state.todaysReservations = action.payload;
    },

    setSales: (state, action) => {
      state.todaysSales = action.payload;
    },

    setVouchers: (state, action) => {
      state.todaysVouchers = action.payload;
    },

    setReceivable: (state, action) => {
      state.receivableSum = action.payload;
    },

    setAppLinkCount: (state, action) => {
      state.appLinkCount = action.payload;
    },

    setQnaCount: (state, action) => {
      state.qnaCount = action.payload;
    },

    setFromTop: (state, action) => {
      state.fromTop = action.payload;
    },

    setErrors: (state, action) => {
      state.errors[action.payload.type] = action.payload.error;
    },

    fetchHomeDataSuccess: (state) => {
      state.loading = false;
    },

    resetHomeData: () => initialState,
  },
});

export const {
  fetchHomeDataStart,
  setReservations,
  setSales,
  setVouchers,
  setReceivable,
  setAppLinkCount,
  setQnaCount,
  setFromTop,
  setErrors,
  fetchHomeDataSuccess,
  resetHomeData,
} = home.actions;

const fetchTodaysReservations =
  (seqPartner, seqPartnerBranch, branchUseYn) => async (dispatch) => {
    if (branchUseYn === "Y" && seqPartnerBranch === 0) return;

    try {
      await api
        .post("/schedules/today", {
          seqPartner,
          seqPartnerBranch,
          searchDate: getCalendar(new Date(), "yyyy-mm-dd"),
        })
        .then(({ data }) => {
          dispatch(
            setReservations({
              appointmentCount: data?.dailyAppointmentCount,
              classCount: data?.dailyClassCount,
              otCount: data?.dailyOtCount,
              consultingCount: data?.dailyConsultingCount,
              etcCount: data?.dailyEtcCount,
            })
          );
        });
    } catch (error) {
      dispatch(setReservations({}));
      dispatch(setErrors({ type: "reservation", error: error.message }));
      console.error(error);
    }
  };

const fetchTodaysSales =
  (seqPartner, seqPartnerBranch, branchUseYn) => async (dispatch) => {
    try {
      await api
        .post("/sales/period/daily-sales", {
          seqPartner,
          seqPartnerBranch,
          searchDate: getCalendar(new Date(), "yyyy-mm-dd"),
          searchDateCount: 1,
          branchUseYn,
        })
        .then(({ data }) => {
          dispatch(
            setSales({
              totalCount: data?.searchTotalSalesCount,
              totalAmount: data?.searchTotalSalesAmount,
              bpayCount: data?.searchBpaySalesCount,
              bpayAmount: data?.searchBpaySalesAmount,
              crmCount: data?.searchCrmSalesCount,
              crmAmount: data?.searchCrmSalesAmount,
              refundCount: data?.searchRefundCount,
              refundAmount: data?.searchRefundAmount,
            })
          );
        });
    } catch (error) {
      dispatch(setSales({}));
      dispatch(setErrors({ type: "sales", error: error.message }));
      console.error(error);
    }
  };

const fetchTodaysVouchers =
  (seqPartner, seqPartnerBranch, branchUseYn) => async (dispatch) => {
    try {
      await api
        .post("/sales/services/summary", {
          seqPartner,
          seqPartnerBranch,
          branchUseYn,
          startDate: getCalendar(new Date(), "yyyy-mm-dd"),
          endDate: getCalendar(new Date(), "yyyy-mm-dd"),
        })
        .then(({ data }) => {
          dispatch(setVouchers(data));
        });
    } catch (error) {
      dispatch(setVouchers({}));
      dispatch(setErrors({ type: "vouchers", error: error.message }));
      console.error(error);
    }
  };

const fetchReceivableAmount =
  (seqPartner, seqPartnerBranch, branchUseYn) => async (dispatch) => {
    try {
      await api
        .post("/sales/accumulate-receivables", {
          seqPartner,
          seqPartnerBranch,
          branchUseYn,
        })
        .then(({ data }) => {
          dispatch(setReceivable(data.accumulateReceivables));
        });
    } catch (error) {
      dispatch(setReceivable(null));
      dispatch(setErrors({ type: "receivable", error: error.message }));
      console.error(error);
    }
  };

const fetchAppLinkCount =
  (seqPartner, seqPartnerBranch) => async (dispatch) => {
    try {
      await api
        .post("/member-app/interlock/count", {
          seqPartner,
          seqPartnerBranch,
        })
        .then(({ data }) => {
          dispatch(setAppLinkCount(data.count));
        });
    } catch (error) {
      dispatch(setAppLinkCount(null));
      dispatch(setErrors({ type: "appLinkCount", error: error.message }));
      console.error(error);
    }
  };

const fetchQuestionCount =
  (seqPartner, seqPartnerBranch) => async (dispatch) => {
    try {
      await api
        .get(`/qna/current-month/${seqPartner}/${seqPartnerBranch}`)
        .then(({ data }) => {
          dispatch(setQnaCount(data));
        });
    } catch (error) {
      dispatch(setQnaCount(null));
      dispatch(setErrors({ type: "qnaCount", error: error.message }));
      console.error(error);
    }
  };

export const fetchHomeData = () => async (dispatch, getState) => {
  try {
    const { seqPartner, seqPartnerBranch, branchUseYn } = getState().partner;

    if (seqPartner) {
      dispatch(fetchHomeDataStart()); // loading = true

      await Promise.all([
        dispatch(fetchTodaysReservations(seqPartner, seqPartnerBranch, branchUseYn)), // 오늘의 예약
        dispatch(fetchTodaysSales(seqPartner, seqPartnerBranch, branchUseYn)), // 오늘의 매출
        dispatch(fetchTodaysVouchers(seqPartner, seqPartnerBranch, branchUseYn)), // 오늘의 이용권 종류별 매출
        dispatch(fetchReceivableAmount(seqPartner, seqPartnerBranch, branchUseYn)), // 누적 미수금
        dispatch(fetchAppLinkCount(seqPartner, seqPartnerBranch)), // 회원 앱 연동
        dispatch(fetchQuestionCount(seqPartner, seqPartnerBranch)), // 문의 사항
      ]);

      dispatch(fetchHomeDataSuccess()); // success (loading = false)
    }
  } catch (error) {
    dispatch(resetHomeData());
    console.error(error);
  }
};

export default home.reducer;
